import React, { useEffect, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { Spinner } from 'react-angle-dashboard-components'
import { useQueryClient } from 'react-query'
import { z } from 'zod'
import {
  useHistory,
  useLocation,
  Route,
  Switch,
  Redirect
} from 'react-router-dom'

import { Layout } from '../../layout'
import { Paths } from '../../routes/paths'
import CensusInfo from './steps/CensusPlanInfo/CensusInfo'
import PlanInfoStep1 from './steps/PlanInfo/PlanInfoStep1'
import QuoteDetailsStep1 from './steps/QuoteDetails/QuoteDetailsStep1'
import QuoteReview from './steps/QuoteReview'
import StepNav from './StepNav'
import BasicInfoStep1 from './steps/BasicQuoteInfo/BasicInfoStep1'
import BasicInfoStep2 from './steps/BasicQuoteInfo/BasicInfoStep2'
import BasicInfoStep3 from './steps/BasicQuoteInfo/BasicInfoStep3'

import PlanInfoStep2 from './steps/PlanInfo/PlanInfoStep2'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  newQuoteFormSchema,
  basicInfoSchema,
  planInfoSchema,
  quoteDetailsSchema,
  censusInfoSchema
} from './schemas'
import QuoteDetailsStep2 from './steps/QuoteDetails/QuoteDetailsStep2'
import { isSchemaValid } from './helper'
import {
  useCensusInfo,
  usePastMedicalPlans,
  useQuote
} from '../../hooks/quote.hooks'
import {
  mapCensusDTOtoForm,
  mapDTOToPastMedPlanForm,
  mapQuoteDtoToForm
} from '../../services/quote/mappers'
import { IQuoteStatus } from '../../services/quote/dto'

export type IQuoteFormData = z.infer<typeof newQuoteFormSchema>

const QuoteForm = () => {
  const history = useHistory()
  const location = useLocation()
  const queryClient = useQueryClient()

  const [currentQuoteStatus, setCurrentQuoteStatus] =
    useState<IQuoteStatus>('draft')

  const queryParams = new URLSearchParams(location.search)

  const quoteIdParam = queryParams.get('quoteId')

  const { data: currentQuoteData, isLoading: currentQuoteDataLoading } =
    useQuote(quoteIdParam)
  const { data: currentCensusInfo, isLoading: currentCensusInfoLoading } =
    useCensusInfo(quoteIdParam)
  const { data: currentPastMedPlans, isLoading: currentPastMedPlansLoading } =
    usePastMedicalPlans(quoteIdParam)

  if (
    currentQuoteData &&
    currentQuoteData.status !== 'draft' &&
    currentQuoteData.status !== 'quoted'
  ) {
    console.log(currentQuoteData)
    // Can't edit quote, go to view
    history.replace(Paths.viewQuote(currentQuoteData.entryId.toString()))
  }

  const isLoading =
    currentQuoteDataLoading &&
    currentCensusInfoLoading &&
    currentPastMedPlansLoading

  // TODO: remove defaults
  const formMethods = useForm<IQuoteFormData>({
    mode: 'onChange',
    resolver: zodResolver(newQuoteFormSchema),
    defaultValues: {
      basicInfo: {
        step1: {},
        step2: {},
        step3: {}
      },
      planInfo: {
        step1: {
          selectedPlans: []
        },
        step2: {}
      },
      censusInfo: [],
      quoteDetails: {
        step1: {
          plans: []
        },
        step2: {
          currentMonthlySpend: null,
          renewalMonthlySpend: null
        }
      }
    }
  })
  const { watch, control, reset } = formMethods

  const quoteId = watch('entryId')?.toString()

  useEffect(() => {
    const resetFormState = () => {
      if (currentQuoteData) {
        const mappedQuote = mapQuoteDtoToForm(currentQuoteData)

        // TODO: remove defaults
        const newFormState = {
          ...mappedQuote,
          basicInfo: {
            ...mappedQuote.basicInfo
          },
          planInfo: {
            ...mappedQuote.planInfo
          },
          censusInfo: mapCensusDTOtoForm(currentCensusInfo),
          quoteDetails: {
            ...mappedQuote.quoteDetails,
            step1: {
              ...mappedQuote.quoteDetails?.step1,
              plans:
                currentPastMedPlans?.map((plan) =>
                  mapDTOToPastMedPlanForm(plan)
                ) ?? []
            }
          }
        }
        reset(newFormState)
      }
    }

    if (currentQuoteData) {
      if (!watch('entryId') && !!currentCensusInfo && !!currentPastMedPlans) {
        // reset form state based on query param in case we come to a page from a refresh or direct link
        resetFormState()
      } else if (
        currentQuoteStatus !== 'draft' &&
        currentQuoteData.status === 'draft'
      ) {
        console.warn('status changed, resetting to api values...')
        // reset form state because ID's have changed due to how the backend copies data from an existing quote
        // into a new quote when the status changes back to draft
        resetFormState()
        // also clear all react query caches
        queryClient.invalidateQueries()
        queryClient.clear()
      } else {
        resetFormState()
      }
      setCurrentQuoteStatus(currentQuoteData.status)
    }
  }, [
    currentCensusInfo,
    currentQuoteData,
    currentPastMedPlans,
    currentQuoteStatus,
    queryClient,
    reset,
    watch
  ])

  // makes sure this component performs a render each time the form changes
  useWatch({ control })

  const steps = [
    {
      title: 'Basic Quote Info',
      onClick: () => {
        history.push(Paths.quoteFormBasicInfo(quoteId))
      },
      active: location.pathname.includes(Paths.quoteFormBasicInfo()),
      // we can always nav to the first step
      disabled: false
    },
    {
      title: 'Plan Info',
      onClick: () => {
        history.push(Paths.quoteFormPlanInfo(quoteId))
      },
      active: location.pathname.includes(Paths.quoteFormPlanInfo()),
      // to be able to use this nav, we need the step before to be completed
      disabled: !isSchemaValid(watch('basicInfo'), basicInfoSchema)
    },
    {
      title: 'Census Plan Info',
      onClick: () => {
        history.push(Paths.quoteFormCensusInfo(quoteId))
      },
      active: location.pathname.includes(Paths.quoteFormCensusInfo()),
      disabled: !isSchemaValid(watch('planInfo'), planInfoSchema)
    },
    {
      title: 'Quote Details',
      onClick: () => {
        history.push(Paths.quoteFormQuoteDetails(quoteId))
      },
      active: location.pathname.includes(Paths.quoteFormQuoteDetails()),
      disabled: !isSchemaValid(watch('censusInfo'), censusInfoSchema)
    },
    {
      title: 'Quote Review',
      onClick: () => {
        history.push(Paths.quoteFormQuoteReview(quoteId))
      },
      active: location.pathname.includes(Paths.quoteFormQuoteReview()),
      disabled: !isSchemaValid(watch('quoteDetails'), quoteDetailsSchema)
    }
  ]

  return (
    <>
      <Layout
        showUserDropdown
        breadcrumbs={[
          {
            title: 'Home',
            link: Paths.home
          },
          {
            title: 'Quotes',
            link: Paths.quotes
          },
          {
            title: quoteId ? 'Edit Quote' : 'New Quote',
            link: Paths.quoteForm(quoteId)
          }
        ]}
        RightSubNavElement={
          <div
            className="d-flex justify-content-between align-items-center px-3"
            style={{
              backgroundColor: '#efefef'
            }}
          >
            {!!quoteId && `Quote ID: ${quoteId}`}
          </div>
        }
      >
        <StepNav steps={steps} />
        {isLoading ? (
          <Spinner animation="grow" />
        ) : (
          <FormProvider {...formMethods}>
            <div
              style={{
                marginBottom: 80
              }}
            >
              <Switch>
                <Route
                  exact
                  path={Paths.quoteForm(quoteId)}
                  component={() => (
                    <Redirect to={Paths.quoteFormBasicInfo(quoteId)} />
                  )}
                />
                <Route exact path={Paths.quoteFormBasicInfo()}>
                  <Redirect to={Paths.basicInfoStep1(quoteId)} />
                </Route>
                <Route path={Paths.basicInfoStep1()}>
                  <BasicInfoStep1 />
                </Route>
                <Route path={Paths.basicInfoStep2()}>
                  <BasicInfoStep2 />
                </Route>
                <Route path={Paths.basicInfoStep3()}>
                  <BasicInfoStep3 />
                </Route>
                <Route exact path={Paths.quoteFormPlanInfo()}>
                  <Redirect to={Paths.quoteFormPlanInfoStep1(quoteId)} />
                </Route>
                <Route
                  path={Paths.quoteFormPlanInfoStep1()}
                  component={PlanInfoStep1}
                />
                <Route
                  path={Paths.quoteFormPlanInfoStep2()}
                  component={PlanInfoStep2}
                />

                <Route
                  path={Paths.quoteFormCensusInfo()}
                  component={CensusInfo}
                />

                <Route exact path={Paths.quoteFormQuoteDetails()}>
                  <Redirect to={Paths.quoteFormQuoteDetailsStep1(quoteId)} />
                </Route>
                <Route
                  path={Paths.quoteFormQuoteDetailsStep1()}
                  component={QuoteDetailsStep1}
                />
                <Route
                  path={Paths.quoteFormQuoteDetailsStep2()}
                  component={QuoteDetailsStep2}
                />
                <Route
                  path={Paths.quoteFormQuoteReview()}
                  component={QuoteReview}
                />
              </Switch>
            </div>
          </FormProvider>
        )}
      </Layout>
    </>
  )
}

export default QuoteForm
